import { useEffect, useState, useCallback, useRef } from 'react';
import { useStore } from '@/lib/store';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { ChevronLeft, X, Sparkles } from 'lucide-react';
import { DISCOVERY_STEPS } from './steps';
import { cn } from '@/lib/utils';
import { useNavigate, useLocation } from 'react-router-dom';
import { calculateCardPosition } from './positioning';
import { uploadLocalDataToCloud } from '@/services/cloudSync';
import { suppressWalkthrough } from '@/lib/first-run-preferences';

export function GhostOverlay() {
    // 1. SELECTORS - Subscription to specific store branches to avoid re-rendering on every store change
    const active = useStore(state => state.discovery.active);
    const currentStepIdx = useStore(state => state.discovery.currentStepIdx);
    const persona = useStore(state => state.discovery.persona);
    const setDiscoveryActive = useStore(state => state.setDiscoveryActive);
    const setDiscoveryStep = useStore(state => state.setDiscoveryStep);
    const markStepDiscovered = useStore(state => state.markStepDiscovered);
    const setDiscoveryPersona = useStore(state => state.setDiscoveryPersona);
    const resetDiscoveryProgress = useStore(state => state.resetDiscoveryProgress);
    const setPreferences = useStore(state => state.setPreferences);

    // 2. DYNAMIC MEASUREMENTS
    const [cardSize, setCardSize] = useState({ width: 320, height: 400 });
    const [spotlightRect, setSpotlightRect] = useState<DOMRect | null>(null);
    const cardRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();
    const location = useLocation();
    const activeStep = DISCOVERY_STEPS[currentStepIdx];

    // Track card size for accurate positioning across step changes.
    // The card remounts per step (`key={activeStep.id}`), so we must re-bind the observer each step.
    useEffect(() => {
        if (!active || !cardRef.current) return;

        const node = cardRef.current;
        const updateFromNode = () => {
            const rect = node.getBoundingClientRect();
            if (rect.width > 0 && rect.height > 0) {
                setCardSize({
                    width: rect.width,
                    height: rect.height,
                });
            }
        };

        updateFromNode();
        const rafId = requestAnimationFrame(updateFromNode);

        const observer = new ResizeObserver(() => {
            updateFromNode();
        });
        observer.observe(node);

        return () => {
            cancelAnimationFrame(rafId);
            observer.disconnect();
        };
    }, [active, activeStep?.id]);

    // Robust spotlight finder with retries for late-rendering elements (tabs, modals)
    const findSpotlightElement = useCallback(() => {
        if (!active || !activeStep) return null;
        return document.querySelector(`[data-tour="${activeStep.targetId}"]`) ||
            document.getElementById(activeStep.targetId);
    }, [active, activeStep]);

    // Use a ref to hold updateSpotlight to avoid "accessed before declaration" issue in setTimeout
    const updateSpotlightRef = useRef<(retries?: number) => void>(() => { });

    const updateSpotlight = useCallback((retries = 3) => {
        if (!active || !activeStep) return;

        const el = findSpotlightElement();
        const rect = el?.getBoundingClientRect();

        // Ensure element exists, is in DOM, and has valid dimensions (not hidden)
        if (el && rect && rect.width > 0 && rect.height > 0) {
            const viewportWidth = window.visualViewport?.width ?? window.innerWidth;
            const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
            const viewportPadding = 12;
            const isOutsideViewport =
                rect.right < viewportPadding ||
                rect.left > viewportWidth - viewportPadding ||
                rect.bottom < viewportPadding ||
                rect.top > viewportHeight - viewportPadding;

            // If target exists but is outside the viewport, center it first and retry.
            if (isOutsideViewport && retries > 0) {
                el.scrollIntoView({
                    block: 'center',
                    inline: 'center',
                    behavior: 'auto',
                });
                setTimeout(() => updateSpotlightRef.current(retries - 1), 120);
                return;
            }

            setSpotlightRect(rect);
            markStepDiscovered(activeStep.id);
        } else if (retries > 0) {
            // More frequent retries (100ms) for smoother "snapping"
            // Use ref to access the function to avoid closure issues
            setTimeout(() => updateSpotlightRef.current(retries - 1), 100);
        } else {
            // Target truly not found after retries
            console.warn(`[Tour] Highlight target not found: ${activeStep.targetId}. Falling back to center.`);
            setSpotlightRect(null);
        }
    }, [active, activeStep, markStepDiscovered, findSpotlightElement]);

    // Keep the ref in sync with the callback
    useEffect(() => {
        updateSpotlightRef.current = updateSpotlight;
    }, [updateSpotlight]);

    // Unified effect for step changes, navigation, and activation
    useEffect(() => {
        if (!active || !activeStep) {
            // This setState is intentional cleanup when tour becomes inactive
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setSpotlightRect(null);
            return;
        }

        let delay = 0;
        let isMoving = false;

        // 1. Handle Routing
        if (activeStep.route) {
            const targetRoute = activeStep.route;
            const currentPath = location.pathname;

            // Robust path comparison (ignore trailing slashes)
            const normalize = (path: string) => path.replace(/\/$/, '') || '/';
            const isAtTarget = normalize(currentPath) === normalize(targetRoute);

            if (!isAtTarget) {
                console.log(`[Tour] Navigating from ${currentPath} to ${targetRoute}`);
                navigate(targetRoute);
                delay = 600; // Increased fixed delay for page load
                isMoving = true;
            } else {
                delay = 150; // Base delay for DOM stability
            }
        } else {
            delay = 150;
        }

        const runStepLogic = () => {
            if (activeStep.tabTriggerId) {
                const trigger = document.querySelector(`[data-tour="${activeStep.tabTriggerId}"]`) ||
                    document.getElementById(activeStep.tabTriggerId);

                if (trigger instanceof HTMLElement) {
                    const isActive = trigger.getAttribute('data-state') === 'active' ||
                        trigger.classList.contains('active') ||
                        trigger.ariaSelected === 'true';

                    if (!isActive) {
                        trigger.focus();
                        trigger.click();
                        trigger.dispatchEvent(new MouseEvent('mousedown', { bubbles: true }));
                        trigger.dispatchEvent(new MouseEvent('mouseup', { bubbles: true }));
                    }

                    const isAnalysis = activeStep.id.includes('chart') || activeStep.id.includes('analysis') || activeStep.id.includes('ring');
                    // More retries (30) with a slightly delayed start (600ms) for heavy analysis pages
                    setTimeout(() => updateSpotlight(isAnalysis ? 30 : 15), isAnalysis ? 600 : 200);
                } else {
                    updateSpotlight(25);
                }
            } else {
                updateSpotlight(isMoving ? 20 : 10);
            }
        };

        const timer = setTimeout(runStepLogic, delay);

        // Listen for window changes while active
        const handleResize = () => updateSpotlight(0);
        const visualViewport = window.visualViewport;
        window.addEventListener('resize', handleResize);
        window.addEventListener('scroll', handleResize, true);
        visualViewport?.addEventListener('resize', handleResize);
        visualViewport?.addEventListener('scroll', handleResize);

        return () => {
            clearTimeout(timer);
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('scroll', handleResize, true);
            visualViewport?.removeEventListener('resize', handleResize);
            visualViewport?.removeEventListener('scroll', handleResize);
        };
    }, [activeStep?.id, active, navigate, location.pathname, updateSpotlight, findSpotlightElement, activeStep]);

    // Safety: If currentStepIdx is invalid, reset to 0
    useEffect(() => {
        if (active && (!activeStep || currentStepIdx >= DISCOVERY_STEPS.length)) {
            console.warn('[Tour] Invalid step index detected. Resetting to 0.');
            setDiscoveryStep(0);
        }
    }, [active, activeStep, currentStepIdx, setDiscoveryStep]);

    // Safety guard
    if (typeof window !== 'undefined' && window.innerWidth < 1024) return null; // Force disable on mobile (handled by MobileStories)

    if (!active) return null;
    if (!activeStep) return null;

    const cardPosition = calculateCardPosition(spotlightRect, activeStep.position, cardSize);

    const completeWalkthrough = () => {
        setDiscoveryActive(false);
        setPreferences({ hasSeenWalkthrough: true });
        suppressWalkthrough();
        uploadLocalDataToCloud({ includeSettings: true, includeProfile: true }).catch(console.error);
    };

    const handleNext = () => {
        if (currentStepIdx < DISCOVERY_STEPS.length - 1) {
            setDiscoveryStep(currentStepIdx + 1);
        } else {
            completeWalkthrough();
        }
    };

    const handlePrev = () => {
        if (currentStepIdx > 0) {
            setDiscoveryStep(currentStepIdx - 1);
        }
    };

    return (
        <div className="fixed inset-0 z-[150] pointer-events-none">
            {/* Backdrop with SVG Mask for Spotlight */}
            <svg className="absolute inset-0 w-full h-full pointer-events-none">
                <defs>
                    <mask id="spotlight-mask">
                        <rect x="0" y="0" width="100%" height="100%" fill="white" />
                        {spotlightRect && (
                            <motion.rect
                                initial={false}
                                animate={{
                                    x: spotlightRect.x - 8,
                                    y: spotlightRect.y - 8,
                                    width: spotlightRect.width + 16,
                                    height: spotlightRect.height + 16,
                                    rx: 12
                                }}
                                transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                                fill="black"
                            />
                        )}
                    </mask>
                </defs>
                <rect
                    x="0"
                    y="0"
                    width="100%"
                    height="100%"
                    fill="rgba(0,0,0,0.6)"
                    mask="url(#spotlight-mask)"
                    className="backdrop-blur-[2px] pointer-events-auto"
                    onClick={completeWalkthrough}
                />
            </svg>

            {/* Coach Tip Card */}
            <AnimatePresence mode="wait">
                {active && activeStep && (
                    <motion.div
                        key={activeStep.id}
                        ref={cardRef}
                        initial={{
                            opacity: 0,
                            scale: 0.95,
                            ...cardPosition
                        }}
                        animate={{
                            opacity: 1,
                            scale: 1,
                            ...cardPosition
                        }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        className="fixed z-[160] w-[min(24rem,calc(100vw-40px))] pointer-events-auto"
                    >
                        <div
                            className="glass-card p-6 rounded-[2rem] shadow-2xl border-white/20 relative overflow-hidden flex flex-col"
                            style={{ maxHeight: 'calc(100dvh - 40px)' }}
                        >
                            {/* Scrollable Content */}
                            <div className="flex-1 min-h-0 overflow-y-auto pr-1 -mr-1 custom-scrollbar">
                                {/* Accent Glow */}
                                <div className="absolute -top-12 -right-12 w-32 h-32 bg-blue-500/10 blur-3xl rounded-full pointer-events-none" />

                                <div className="flex items-start justify-between mb-4">
                                    <div className="space-y-1">
                                        <div className="flex items-center gap-2">
                                            <Sparkles className="w-4 h-4 text-blue-500" />
                                            <span className="text-[10px] font-black uppercase tracking-[0.2em] text-blue-500/80">Coach Insight</span>
                                        </div>

                                        {/* Preference Toggle - Enhanced for Welcome Step */}
                                        <button
                                            onClick={() => setDiscoveryPersona(persona === 'normal' ? 'tech' : 'normal')}
                                            className={cn(
                                                "flex flex-col items-start gap-0.5 px-3 py-2 rounded-xl border transition-all pointer-events-auto mt-2 w-full",
                                                activeStep.id === 'welcome'
                                                    ? "bg-blue-600 shadow-lg shadow-blue-600/20 text-white border-blue-500 ring-2 ring-blue-500/20"
                                                    : "bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/10 hover:bg-blue-500/20"
                                            )}
                                        >
                                            <span className={cn("text-[8px] uppercase tracking-wider font-bold", activeStep.id === 'welcome' ? "text-white/70" : "opacity-60")}>
                                                Terminology Preference
                                            </span>
                                            <span className="flex items-center gap-1.5 font-black text-[11px]">
                                                {persona === 'normal' ? 'Switch to Tech Mode' : 'Switch to Normal Mode'}
                                            </span>
                                        </button>

                                        <h3 className="text-xl font-black tracking-tight dark:text-white mt-4">
                                            {activeStep?.title}
                                        </h3>
                                    </div>
                                    <button
                                        onClick={(e) => {
                                            console.log('Close button clicked');
                                            e.preventDefault();
                                            e.stopPropagation();
                                            completeWalkthrough();
                                        }}
                                        className="relative z-[170] flex items-center justify-center rounded-full h-8 w-8 hover:bg-slate-100 dark:hover:bg-white/10 transition-colors text-slate-400 hover:text-slate-600 dark:hover:text-white pointer-events-auto shrink-0"
                                        aria-label="Close guide"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                </div>

                                {activeStep.id === 'welcome' && (
                                    <div className="mb-4 text-xs font-bold text-blue-600 dark:text-blue-400 p-2 rounded-lg bg-blue-50 dark:bg-blue-900/20">
                                        Tip: You can change this anytime during the tour!
                                    </div>
                                )}

                                <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed min-h-[4rem]">
                                    {persona === 'normal'
                                        ? activeStep?.description.normal
                                        : activeStep?.description.tech}
                                </p>
                            </div>

                            {/* Footer Section */}
                            <div className="mt-8 space-y-6 pt-4 border-t border-white/10 shrink-0">
                                {/* Progress Indicators Row */}
                                <div className="flex justify-center gap-1.5">
                                    {DISCOVERY_STEPS.map((_, i) => (
                                        <div
                                            key={i}
                                            className={cn(
                                                "h-1 rounded-full transition-all duration-300",
                                                i === currentStepIdx ? "w-6 bg-blue-500" : "w-1.5 bg-slate-300/30"
                                            )}
                                        />
                                    ))}
                                </div>

                                {/* Navigation Buttons Row */}
                                <div className="flex items-center justify-between gap-2">
                                    <div className="flex gap-2">
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={handlePrev}
                                            disabled={currentStepIdx === 0}
                                            className="rounded-full h-10 w-10 border border-white/5 hover:bg-white/10"
                                        >
                                            <ChevronLeft className="w-5 h-5" />
                                        </Button>

                                        {(currentStepIdx === DISCOVERY_STEPS.length - 1 || !spotlightRect) && (
                                            <Button
                                                variant="ghost"
                                                onClick={() => {
                                                    setDiscoveryStep(0);
                                                    resetDiscoveryProgress();
                                                }}
                                                className="rounded-full px-3 text-[10px] font-black uppercase tracking-wider text-slate-400 hover:text-blue-500 hover:bg-blue-500/5 transition-all"
                                            >
                                                Start Over
                                            </Button>
                                        )}
                                    </div>

                                    <Button
                                        onClick={handleNext}
                                        className="rounded-full px-6 bg-blue-600 hover:bg-blue-700 text-white font-black uppercase tracking-wider text-[11px] h-10 shadow-lg shadow-blue-600/20 active:scale-95 transition-all"
                                    >
                                        {currentStepIdx === DISCOVERY_STEPS.length - 1 ? 'Finish' : 'Next'}
                                    </Button>
                                </div>

                                {/* Skip Button - Very Bottom */}
                                <div className="flex justify-center pt-2">
                                    <button
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            completeWalkthrough();
                                        }}
                                        className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500/60 hover:text-blue-500 transition-all duration-300 hover:tracking-[0.3em] py-2"
                                    >
                                        Skip, I know the app
                                    </button>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
